import React, { useCallback, useEffect, useState } from "react";
import toast from "react-hot-toast";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import debounce from "lodash/debounce";
import { Plus, Users } from "lucide-react";

import { Dialog } from "components/ui/Dialog";
import { TabSwitcher } from "components/ui/TabSwitcher";
import { SmsModal2 } from "features/comunications/components/SmsModal2/SmsModal2";
import { useMyPaginatedPatients } from "features/patients/hooks/useMyPaginatedPatients";
import { usePaginatedPatients } from "features/patients/hooks/usePaginatedPatients";
import { usePatientCounts } from "features/patients/hooks/usePatientCounts";
import { AddPatientModal, EditPatientModal } from "features/patients/modals";
import { PatientsTable } from "features/patients/tables";
import { pathGenerators } from "utils/routes";

import { clearPreviousPageForEditPatient } from "../../../redux/patient/PatientSlice";
import styles from "./PatientPage.module.css";

const PatientPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { state } = location;

  let tenantVal = useSelector((state) => state.auth.user?.tenant);
  if (!tenantVal) tenantVal = localStorage.getItem("kinesin-tenant");

  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [inputValue, setInputValue] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [isSmsModalOpen, setIsSmsModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [patient, setPatient] = useState({});
  const [editPatient, setEditPatient] = useState(null);
  const [activeTab, setActiveTab] = useState("ALL");

  const { data: allPatientsData, isLoading: isLoadingAllPatients } =
    usePaginatedPatients(
      tenantVal,
      currentPage,
      searchQuery,
      rowsPerPage,
      activeTab === "ALL"
    );

  const { data: myPatientsData, isLoading: isLoadingMyPatients } =
    useMyPaginatedPatients(
      tenantVal,
      currentPage,
      searchQuery,
      rowsPerPage,
      activeTab === "MY"
    );

  const { data: patientCounts } = usePatientCounts(tenantVal);

  const data = activeTab === "ALL" ? allPatientsData : myPatientsData;
  const isLoading =
    activeTab === "ALL" ? isLoadingAllPatients : isLoadingMyPatients;

  const paginatedPatients = data?.data || [];
  const totalRecords = data?.totalRecords || 0;

  useEffect(() => {
    if (state && state.searchQuery) {
      setInputValue(state.searchQuery);
      setSearchQuery(state.searchQuery);
    }
    if (state && state.showToast) {
      toast.success("Patient updated successfully");
    }
  }, [state]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSetSearch = useCallback(
    debounce((query) => {
      setSearchQuery(query);
      setCurrentPage(1);
    }, 400),
    []
  );

  const handleSearchChange = (value) => {
    setInputValue(value);
    debouncedSetSearch(value);
  };

  const toggleSmsModal = (patient) => {
    setPatient(patient);
    setIsSmsModalOpen(!isSmsModalOpen);
  };

  const toggleSmsClose = () => {
    setIsSmsModalOpen(false);
  };

  const handleTabChange = (tabKey) => {
    setActiveTab(tabKey);
    setCurrentPage(1);
  };

  const goToPatientDetails = (id) => {
    navigate(pathGenerators.patient.details(id));
  };

  const handleEditPatient = (patientToEdit) => {
    setEditPatient(patientToEdit);
    setIsEditModalOpen(true);
  };

  const handleEditModalClose = () => {
    setIsEditModalOpen(false);
    setEditPatient(null);
  };

  return (
    <div>
      <TabSwitcher
        options={[
          {
            value: "ALL",
            label: "All Patients",
            icon: <Users size={20} strokeWidth={2} />,
            count: patientCounts?.ALL || 0,
          },
          {
            value: "MY",
            label: "My Patients",
            icon: <Users size={20} strokeWidth={2} />,
            count: patientCounts?.MY || 0,
          },
        ]}
        value={activeTab}
        onChange={handleTabChange}
        fullWidth
        marginTop={16}
        marginBottom={24}
      />
      <div className={styles.patientList}>
        <div className={styles.header}>
          <p className={styles.title}>Patients</p>
          <div className={styles.headerActions}>
            <button
              className={styles.addButton}
              onClick={() => {
                dispatch(clearPreviousPageForEditPatient());
                setIsAddModalOpen(true);
              }}
            >
              <span>Add patient</span>
              <Plus size={18} />
            </button>
          </div>
        </div>

        <PatientsTable
          patients={paginatedPatients}
          isLoading={isLoading}
          searchQuery={inputValue}
          onSearchChange={handleSearchChange}
          onNameClick={(p) => goToPatientDetails(p.id)}
          onEdit={handleEditPatient}
          onSendSms={toggleSmsModal}
          totalRecords={totalRecords}
          currentPage={currentPage}
          onPageChange={setCurrentPage}
          rowsPerPage={rowsPerPage}
          onRowsChange={(val) => {
            setRowsPerPage(val);
            setCurrentPage(1);
          }}
          emptyText={
            searchQuery
              ? "Try adjusting your search to find what you're looking for."
              : "You don't have any patients at this time."
          }
          searchPlaceholder="Search"
        />

        <Dialog
          open={isSmsModalOpen}
          onClose={toggleSmsClose}
          title={"Send sms"}
          width={1000}
        >
          <SmsModal2
            tenantVal={tenantVal}
            patient={patient}
            onClose={toggleSmsModal}
          />
        </Dialog>

        <EditPatientModal
          patient={editPatient}
          isOpen={isEditModalOpen}
          onClose={handleEditModalClose}
        />

        <AddPatientModal
          isOpen={isAddModalOpen}
          onClose={() => setIsAddModalOpen(false)}
          onPatientCreated={(patientId) => {
            navigate(pathGenerators.patient.details(String(patientId)));
          }}
        />
      </div>
    </div>
  );
};

export { PatientPage };
